import React, { useEffect, useState, useRef } from 'react';
import styled, { useTheme } from 'styled-components';

import { GetInternalLinkFunctionType } from '@repo/models/GetInternalLinkFunctionType';
import type { SanityInternalLink } from '@repo/models/gatsby-graphql-types';

import LinkComponent from '@repo/ui-components/LinkComponent';
import breakpoints from '@repo/ui-components/breakpoints';
import getTopLevelMenuItemLink, {
  getCrawlableTopLevelMenuItemLink,
} from '@repo/ui-components/header/getTopLevelMenuItemLink';
import ChevronRight from '../svgs/chevron-right.svg';

interface NavDesktopProps {
  navLinks: any[];
  megaMenuActive: number | null;
  setKeyboardNavActive: (active: boolean) => void;
  setMegaMenuActive: (index: number | null) => void;
  keyboardNavActive: boolean;
  getInternalLink?: GetInternalLinkFunctionType;
}

const Wrapper = styled.nav`
  display: none;
  height: 90px;

  @media (min-width: ${breakpoints.lg}) {
    display: flex;
    justify-content: center;
    flex: 1;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;

  /* Preserve list semantics for screen readers when list-style is none */
  &::before {
    content: '';
    position: absolute;
  }
`;

const NavDesktopMenuItem = styled.li`
  display: flex;
  cursor: pointer;
  align-items: center;
  font-size: ${({ theme }) => theme.navigation.NavDesktopMenuItemFontSize};
  font-weight: inherit;
  margin-bottom: -1rem;
  padding: 0.5rem 0.625rem;
  margin-top: -1rem;
  text-transform: ${({ theme }) => theme.navigation.mainItemTextTransform};
  &:first-child {
    padding-left: 5rem;
    margin-left: -5rem;
  }
  svg {
    transition: all 0.5s ease;
    fill: ${({ theme }) => theme.colors.defaultFont};
  }
  a {
    transition: all 0.5s ease;
    text-decoration: none;
    &:hover {
      text-decoration: underline;

      svg {
        fill: ${({ theme }) => theme.colors.defaultFont};
      }
    }
  }
`;

const NavDesktopLink = styled(LinkComponent)`
  display: flex;
  align-self: stretch;
  align-items: center;
  color: inherit;
  font-weight: ${({ theme }) => theme.navigation.fontWeight};
  padding: 0.25rem;
  text-decoration: none;
`;

const DropdownSubMenu = styled.ul<{ $subMenuActive: boolean }>`
  position: absolute;
  background: ${({ theme }) => theme.colors.background};
  top: -0.25rem;
  left: 14rem;
  border: solid 1px inherit;
  width: 15rem;
  margin: 0;
  padding: 0;
  display: ${props => (props.$subMenuActive ? 'block' : 'none')};
  pointer-events: auto;
  box-shadow:
    rgba(0, 0, 0, 0.02) 0 2.8px 2.2px,
    rgba(0, 0, 0, 0.027) 0 6.7px 5.3px,
    rgba(0, 0, 0, 0.035) 0 12.5px 10px,
    rgba(0, 0, 0, 0.043) 0 22.3px 17.9px,
    rgba(0, 0, 0, 0.05) 0 41.8px 33.4px,
    rgba(0, 0, 0, 0.07) 0 100px 80px;
`;

interface DropdownMenuProps {
  $dropdownActive: boolean;
}

const DropdownMenu = styled.div<DropdownMenuProps>`
  border: solid 1px inherit;
  display: ${props => (props.$dropdownActive ? 'flex' : 'none')};
  background: ${({ theme }) => theme.colors.background};
  position: absolute;
  top: 122px;
  z-index: 1000;
  box-shadow:
    rgba(0, 0, 0, 0.02) 0 2.8px 2.2px,
    rgba(0, 0, 0, 0.027) 0 6.7px 5.3px,
    rgba(0, 0, 0, 0.035) 0 12.5px 10px,
    rgba(0, 0, 0, 0.043) 0 22.3px 17.9px,
    rgba(0, 0, 0, 0.05) 0 41.8px 33.4px,
    rgba(0, 0, 0, 0.07) 0 100px 80px;
  color: inherit; // make prop?
  width: 15rem;
  pointer-events: auto;

  ul {
    list-style: none;
    padding: 0.25rem 0;
    margin: 0;
    width: 100%;

    li {
      margin: 0;
      padding: 0;
      position: relative;
      z-index: 2;

      &:hover,
      &:focus {
        ${DropdownSubMenu} {
          display: block;
        }
      }

      a {
        font-size: 1rem;
        display: block;
        text-decoration: none;
        font-weight: inherit;
        padding: 0.5rem 1rem;
        transition: all 0.5s ease;
        color: inherit;

        &:hover {
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }

  @media (max-width: calc(${breakpoints.lg} - 1)) {
    display: none;
  }
`;

const NavDesktopButton = styled.button`
  display: flex;
  align-items: center;
  background: inherit;
  align-self: stretch;
  font-weight: ${({ theme }) => theme.navigation.fontWeight};
  border: 0;
  color: inherit;
  padding: 0.5rem;
  text-decoration: none;
  text-transform: ${({ theme }) => theme.navigation.mainItemTextTransform};
  cursor: pointer;
  pointer-events: auto;
  svg {
    margin-left: ${({ theme }) => theme.navigation.downArrowSpacing};
  }

  a {
    color: inherit;
    font-weight: inherit;
  }
`;

interface SubSection {
  title: string;
  link?: {
    href?: string;
    blank?: boolean;
    internalLink?: SanityInternalLink;
  };
}

interface DropdownSubMenuItemsProps {
  items: SubSection[];
  subMenuActive: boolean;
  setSubMenuActive: (active: boolean) => void;
  setDropdownActive: (active: string | null) => void;
  getInternalLink: (link: any) => string;
}

interface LinkData {
  href?: string;
  linkType?: string;
  internalLink?: SanityInternalLink;
}

interface DropdownMenuLinkItemProps {
  menuItem: {
    title: string;
    link?: LinkData;
    items?: any[];
  };
  isLastItem: boolean;
  subMenuActive: boolean;
  setSubMenuActive: (active: boolean) => void;
  setDropdownActive: (active: string | null) => void;
  getInternalLink: (link: any) => string;
}

interface Link {
  href?: string;
  linkType?: string;
  blank?: boolean;
  internalLink?: SanityInternalLink;
}

interface MenuItem {
  title: string;
  link?: Link;
  items?: MenuItem[];
}

interface NavLink {
  title: string;
  menuType: string;
  items: MenuItem[];
}

interface DropdownMenuItemsProps {
  navLink: NavLink;
  dropdownActive: string | null;
  subMenuActive: boolean;
  setSubMenuActive: (active: boolean) => void;
  setDropdownActive: (active: string | null) => void;
  getInternalLink: (link: any) => string;
}

const resolveMenuLink = (
  link: LinkData | Link | undefined,
  getInternalLink: (link: any) => string
) => getTopLevelMenuItemLink(link, getInternalLink) ?? '#';

const DropdownSubMenuItems: React.FC<DropdownSubMenuItemsProps> = ({
  items,
  subMenuActive,
  setSubMenuActive,
  setDropdownActive,
  getInternalLink,
}) => {
  const shiftTabPressed = useRef(false);

  if (!items?.length) return null;

  return (
    <DropdownSubMenu $subMenuActive={subMenuActive}>
      {items.map((subSection, index) => (
        <li key={subSection.title}>
          {subSection?.link?.href?.startsWith('http') ? (
            <a
              href={subSection.link.href}
              target={subSection.link.blank ? '_blank' : '_self'}
              rel="noreferrer"
            >
              {subSection.title}
            </a>
          ) : (
            <LinkComponent
              to={
                subSection.link?.internalLink
                  ? getInternalLink({
                      ...subSection.link,
                      reference: subSection.link.internalLink,
                    })
                  : subSection.link?.href
              }
              onKeyDown={event => {
                if (event.key === 'Tab' && event.shiftKey) {
                  shiftTabPressed.current = true;
                } else {
                  shiftTabPressed.current = false;
                }
              }}
              onBlur={() => {
                if (index === items.length - 1 && !shiftTabPressed.current) {
                  setSubMenuActive(false);
                  setDropdownActive(null);
                }
              }}
            >
              {subSection.title}
            </LinkComponent>
          )}
        </li>
      ))}
    </DropdownSubMenu>
  );
};

const DropdownMenuLinkItem: React.FC<DropdownMenuLinkItemProps> = ({
  menuItem,
  isLastItem,
  subMenuActive,
  setSubMenuActive,
  setDropdownActive,
  getInternalLink,
}) => {
  const shiftTabPressed = useRef(false);

  if (menuItem?.link?.linkType === 'external') return null;

  const linkHref = menuItem.link?.internalLink
    ? getInternalLink({
        ...menuItem.link,
        reference: menuItem.link.internalLink,
      })
    : menuItem?.link?.href;

  return (
    <LinkComponent
      to={linkHref}
      style={
        menuItem?.items?.length > 0
          ? { display: 'flex', justifyContent: 'space-between' }
          : undefined
      }
      onFocus={() => {
        if (menuItem.items) setSubMenuActive(true);
      }}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          setSubMenuActive(!subMenuActive);
        }

        if (event.key === 'Tab' && event.shiftKey) {
          shiftTabPressed.current = true;
        } else {
          shiftTabPressed.current = false;
        }
      }}
      onBlur={() => {
        if (isLastItem && !shiftTabPressed.current) {
          setDropdownActive(null);
          setSubMenuActive(false);
        }
      }}
    >
      {menuItem.title}
      {menuItem?.items?.length > 0 && (
        <ChevronRight height={22} width={22} style={{ marginTop: '.25rem' }} />
      )}
    </LinkComponent>
  );
};

const DropdownMenuItems: React.FC<DropdownMenuItemsProps> = ({
  navLink,
  dropdownActive,
  subMenuActive,
  setSubMenuActive,
  setDropdownActive,
  getInternalLink,
}) => {
  const shiftTabPressed = useRef(false);

  if (navLink.menuType !== 'dropdown') return null;

  return (
    <DropdownMenu
      $dropdownActive={dropdownActive === navLink.title}
      data-dropdown-menu="true"
      onMouseEnter={() => {
        setDropdownActive(navLink.title);
      }}
      onMouseLeave={() => {
        setDropdownActive(null);
        setSubMenuActive(false);
      }}
      onBlur={event => {
        const nextTarget = event.relatedTarget as Node | null;
        if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
          setDropdownActive(null);
          setSubMenuActive(false);
        }
      }}
    >
      <ul>
        {navLink.items?.length > 0 &&
          navLink.items.map((menuItem, index) => {
            const isLastItem =
              index === navLink.items.length - 1 && !menuItem.items;

            return (
              <li key={menuItem.title}>
                {menuItem?.link?.linkType === 'external' && (
                  <LinkComponent
                    to={menuItem.link?.href}
                    target={menuItem?.link?.blank ? `_blank` : `_self`}
                    style={
                      menuItem.items
                        ? { display: 'flex', justifyContent: 'space-between' }
                        : undefined
                    }
                    onFocus={() => {
                      if (menuItem.items) setSubMenuActive(true);
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        setSubMenuActive(!subMenuActive);
                      }

                      if (event.key === 'Tab' && event.shiftKey) {
                        shiftTabPressed.current = true;
                      } else {
                        shiftTabPressed.current = false;
                      }
                    }}
                    onBlur={() => {
                      if (isLastItem && !shiftTabPressed.current) {
                        setDropdownActive(null);
                        setSubMenuActive(false);
                      }
                    }}
                  >
                    {menuItem.title}
                    {menuItem?.items?.length > 0 && (
                      <ChevronRight
                        height={22}
                        width={22}
                        style={{ marginTop: '.25rem' }}
                      />
                    )}
                  </LinkComponent>
                )}

                <DropdownMenuLinkItem
                  menuItem={menuItem}
                  isLastItem={isLastItem}
                  subMenuActive={subMenuActive}
                  setSubMenuActive={setSubMenuActive}
                  setDropdownActive={setDropdownActive}
                  getInternalLink={getInternalLink}
                />

                {menuItem.items?.length > 0 && (
                  <DropdownSubMenuItems
                    items={menuItem.items}
                    subMenuActive={subMenuActive}
                    setSubMenuActive={setSubMenuActive}
                    setDropdownActive={setDropdownActive}
                    getInternalLink={getInternalLink}
                  />
                )}
              </li>
            );
          })}
      </ul>
    </DropdownMenu>
  );
};

function NavDesktop({
  navLinks = [],
  megaMenuActive = null,
  setKeyboardNavActive = () => {},
  setMegaMenuActive = () => {},
  keyboardNavActive = false,
  getInternalLink = () => '',
}: Readonly<NavDesktopProps>) {
  const theme = useTheme();
  const shopNavItem = useRef<HTMLButtonElement>(null);
  const [dropdownActive, setDropdownActive] = useState<string | null>(null);
  const [subMenuActive, setSubMenuActive] = useState(false);

  const toggleMenu = (menuType: string, index: number, title: string) => {
    if (menuType === 'megaMenu') {
      setMegaMenuActive(megaMenuActive === index ? null : index);
      return;
    }
    if (menuType === 'dropdown') {
      if (dropdownActive) {
        setDropdownActive(null);
      } else {
        setDropdownActive(title);
      }
    }
  };

  const handleEscape = (menuType: string, title: string) => {
    if (menuType === 'megaMenu') {
      setMegaMenuActive(null);
    }
    if (dropdownActive) {
      setDropdownActive(null);
      return;
    }
    setDropdownActive(title);
  };

  useEffect(() => {
    if (megaMenuActive < 0 && keyboardNavActive) {
      shopNavItem.current?.focus();
    }
  }, [keyboardNavActive, megaMenuActive]);

  return (
    <Wrapper
      onMouseLeave={() => {
        setMegaMenuActive(null);
      }}
    >
      <NavList className="nav-desktop-list">
        {navLinks.map((navLink, megaMenuIndex) => {
          const isInteractiveMenu =
            navLink.menuType === 'megaMenu' || navLink.menuType === 'dropdown';
          const topLevelHref = isInteractiveMenu
            ? getCrawlableTopLevelMenuItemLink(navLink.link, getInternalLink)
            : undefined;

          const buttonProps = topLevelHref
            ? {
                as: LinkComponent,
                to: topLevelHref,
                onClick: (e: React.MouseEvent) => e.preventDefault(),
                role: 'button',
              }
            : {
                as: 'button' as const,
              };

          return (
            <NavDesktopMenuItem
              key={navLink.title}
              onMouseEnter={() => {
                setKeyboardNavActive(false);
                if (navLink.menuType === 'megaMenu') {
                  setMegaMenuActive(megaMenuIndex);
                  setDropdownActive(null);
                }
                if (navLink.menuType === 'dropdown') {
                  setDropdownActive(navLink.title);
                  setMegaMenuActive(null);
                }
                if (navLink.menuType === 'static') {
                  setMegaMenuActive(null);
                  setDropdownActive(null);
                }
              }}
              onClick={() => {
                toggleMenu(navLink.menuType, megaMenuIndex, navLink.title);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  if (event.key === ' ') {
                    event.preventDefault();
                    toggleMenu(navLink.menuType, megaMenuIndex, navLink.title);
                  }
                  setKeyboardNavActive(true);
                  return;
                }
                if (event.key === 'Escape') {
                  handleEscape(navLink.menuType, navLink.title);
                }
              }}
            >
              {navLink.menuType === 'megaMenu' && (
                <NavDesktopButton
                  id={`menu-trigger-${megaMenuIndex}`}
                  ref={shopNavItem}
                  aria-expanded={
                    megaMenuActive === megaMenuIndex ||
                    dropdownActive === navLink.title
                  }
                  {...buttonProps}
                >
                  {navLink.title}
                  <theme.navigation.DownArrow
                    width={theme.navigation.downArrowSize}
                    height={theme.navigation.downArrowSize}
                  />
                </NavDesktopButton>
              )}
              {navLink.menuType === 'dropdown' && (
                <NavDesktopButton
                  id={`menu-trigger-${megaMenuIndex}`}
                  onFocus={() => {
                    setMegaMenuActive(null);
                    if (dropdownActive === navLink.title) {
                      setDropdownActive(null);
                    }
                  }}
                  aria-expanded={
                    megaMenuActive === megaMenuIndex ||
                    dropdownActive === navLink.title
                  }
                  aria-haspopup
                  {...buttonProps}
                >
                  {navLink.title}
                  <theme.navigation.DownArrow
                    width={theme.navigation.downArrowSize}
                    height={theme.navigation.downArrowSize}
                  />
                </NavDesktopButton>
              )}
              <DropdownMenuItems
                navLink={navLink}
                dropdownActive={dropdownActive}
                subMenuActive={subMenuActive}
                setSubMenuActive={setSubMenuActive}
                setDropdownActive={setDropdownActive}
                getInternalLink={getInternalLink}
              />
              {navLink.menuType === 'static' && (
                <NavDesktopLink
                  to={resolveMenuLink(navLink.link, getInternalLink)}
                  id={`menu-trigger-${megaMenuIndex}`}
                >
                  {navLink.title}
                </NavDesktopLink>
              )}
            </NavDesktopMenuItem>
          );
        })}
      </NavList>
    </Wrapper>
  );
}

export default NavDesktop;
